import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useWorkspace } from '../context/WorkspaceContext';
import TemplateEditor from '../components/crm/TemplateEditor';
import CampaignBuilder from '../components/crm/CampaignBuilder';
import ChatMonitor from '../components/crm/ChatMonitor';
import {
  DashboardModuleBody,
  DashboardModuleRoot,
  DashboardModuleToolbar,
} from '../components/layout/DashboardLayout';

const TABS = [
  { id: 'chat-monitor', label: 'Chat Monitor', icon: <ChatIcon fontSize="small" /> },
  { id: 'campaigns', label: 'Campaigns', icon: <CampaignOutlinedIcon fontSize="small" /> },
  { id: 'templates', label: 'Templates', icon: <DescriptionOutlinedIcon fontSize="small" /> },
  { id: 'settings', label: 'Settings', icon: <SettingsOutlinedIcon fontSize="small" /> },
];

/**
 * Top-level CRM & Marketing module. Chat monitoring, templates, and campaigns
 * are isolated per app (from the Product Portfolio `apps` collection), selected
 * via the context switcher above the tabs. Chat Monitor works with or without
 * an app selected.
 * @param {{ userData?: { companyId?: string } }} props
 */
export default function CrmHub({ userData }) {
  const { companyId } = useWorkspace();
  const location = useLocation();
  const resolvedCompanyId = companyId || userData?.companyId || null;
  const preselectedAppId =
    typeof location.state?.crmAppId === 'string' ? location.state.crmAppId : null;

  const [tab, setTab] = useState('chat-monitor');
  const [apps, setApps] = useState([]);
  const [activeAppId, setActiveAppId] = useState('');
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsError, setAppsError] = useState('');

  useEffect(() => {
    if (!resolvedCompanyId) return undefined;
    let active = true;
    setAppsLoading(true);
    setAppsError('');
    getDocs(query(collection(db, 'apps'), where('companyId', '==', resolvedCompanyId)))
      .then((snap) => {
        if (!active) return;
        const rows = snap.docs
          .map((d) => ({ id: d.id, appName: d.data()?.appName || 'Untitled app' }))
          .sort((a, b) => a.appName.localeCompare(b.appName));
        setApps(rows);
        const ids = rows.map((r) => r.id);
        setActiveAppId((prev) => {
          if (preselectedAppId && ids.includes(preselectedAppId)) return preselectedAppId;
          if (prev && ids.includes(prev)) return prev;
          return ids[0] || '';
        });
      })
      .catch((e) => {
        if (active) {
          console.error('fetch crm apps:', e);
          setAppsError(e.message || 'Could not load apps.');
        }
      })
      .finally(() => {
        if (active) setAppsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedCompanyId, preselectedAppId]);

  const activeApp = apps.find((a) => a.id === activeAppId) || null;

  const needsAppContext = tab !== 'chat-monitor';

  return (
    <DashboardModuleRoot>
      <DashboardModuleToolbar sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            CRM &amp; Marketing
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Chat monitoring, email templates and campaigns per app
          </Typography>
        </Box>

        {!appsLoading && apps.length > 0 && (
          <TextField
            select
            size="small"
            label="Active app"
            value={activeAppId}
            onChange={(e) => setActiveAppId(e.target.value)}
            sx={{ minWidth: 220, flexShrink: 0 }}
          >
            {apps.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.appName}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DashboardModuleToolbar>

      <Box
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: { xs: 1, sm: 1.5 },
        }}
      >
        <Tabs
          value={tab}
          onChange={(_e, next) => setTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="CRM and marketing sections"
        >
          {TABS.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              label={t.label}
              icon={t.icon}
              iconPosition="start"
              sx={{ minHeight: 48, textTransform: 'none' }}
            />
          ))}
        </Tabs>
      </Box>

      <DashboardModuleBody
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 2,
          pb: 4,
        }}
      >
        {appsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : appsError ? (
          <Alert severity="error">{appsError}</Alert>
        ) : needsAppContext && apps.length === 0 ? (
          <Alert severity="info">
            No apps configured yet. Add one in <strong>Product Portfolio</strong> to create your
            first CRM context. Each app keeps its own templates and campaigns.
          </Alert>
        ) : (
          <>
            {needsAppContext && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Templates and campaigns below are scoped to{' '}
                <strong>{activeApp?.appName || 'the selected app'}</strong>.
              </Typography>
            )}

            <Box role="tabpanel" hidden={tab !== 'chat-monitor'}>
              {tab === 'chat-monitor' && (
                <ChatMonitor
                  userData={userData}
                  activeAppId={activeAppId}
                  activeAppName={activeApp?.appName || ''}
                />
              )}
            </Box>

            <Box role="tabpanel" hidden={tab !== 'campaigns'}>
              {tab === 'campaigns' && (
                <CampaignBuilder
                  userData={userData}
                  activeAppId={activeAppId}
                  activeAppName={activeApp?.appName || ''}
                />
              )}
            </Box>

            <Box role="tabpanel" hidden={tab !== 'templates'}>
              {tab === 'templates' && (
                <TemplateEditor
                  userData={userData}
                  activeAppId={activeAppId}
                  activeAppName={activeApp?.appName || ''}
                />
              )}
            </Box>

            <Box role="tabpanel" hidden={tab !== 'settings'}>
              {tab === 'settings' && (
                <Alert severity="info">
                  Configure outbound email providers per app under{' '}
                  <strong>Settings → Workspace → API &amp; integrations</strong>. Dedicated CRM
                  settings will live here soon.
                </Alert>
              )}
            </Box>
          </>
        )}
      </DashboardModuleBody>
    </DashboardModuleRoot>
  );
}
